const SCROLL_THRESHOLD = 5;
const SCROLL_COOLDOWN_MS = 100;
const HIDE_OFFSET = 20;

class HeaderComponent extends HTMLElement {
  connectedCallback() {
    this.headerNav = this.querySelector('[data-header-nav]');
    this.headerWrapper = document.querySelector('.shopify-section-group-header-group');
    this.announcementBar = document.querySelector('.section-announcement-bar');
    this.search = this.querySelector('[data-header-search]');
    this.searchToggle = this.querySelector('search-toggle');
    this.popovers = this.querySelectorAll('[popover]');
    this.popoverTriggers = this.querySelectorAll('button[popovertarget]');

    if (this.headerWrapper) this.initScroll();
    this.initPopoverTriggers();
  }

  disconnectedCallback() {
    if (this.boundHandleScroll) {
      window.removeEventListener('scroll', this.boundHandleScroll);
    }
    if (this.boundHandleResize) {
      window.removeEventListener('resize', this.boundHandleResize);
    }
  }

  initScroll() {
    this.isScrolling = false;
    this.lastScrollTop = 0;
    this.hideRevealCooldownUntil = 0;

    const announcementHeight = this.announcementBar?.offsetHeight || 0;
    this.scrollOffset = announcementHeight + this.offsetHeight;
    this.headerStart = announcementHeight;

    this.updateCSSVars();

    this.boundHandleResize = debounce(() => {
      this.updateCSSVars();
      this.handleScroll();
    }, 100);

    this.boundHandleScroll = () => {
      if (this.isScrolling) return;
      this.isScrolling = true;
      requestAnimationFrame(() => {
        this.handleScroll();
        this.isScrolling = false;
      });
    };

    window.addEventListener('resize', this.boundHandleResize);
    window.addEventListener('scroll', this.boundHandleScroll, { passive: true });
  }

  updateCSSVars() {
    document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    document.documentElement.style.setProperty('--header-nav-height', `${this.headerNav?.offsetHeight || 0}px`);
    document.documentElement.style.setProperty('--announcement-bar-height', `${this.announcementBar?.offsetHeight || 0}px`);
  }

  handleScroll() {
    const blockedClasses = ['main-menu-is-open', 'cart-drawer-is-open', 'search-is-open'];
    if (blockedClasses.some(cls => document.body.classList.contains(cls))) return;

    const scrollTop = window.scrollY;
    const scrollDelta = scrollTop - this.lastScrollTop;
    const now = Date.now();

    if (scrollTop <= this.headerStart) {
      this.revealTop();
    }
    else if (!document.body.classList.contains('desktop-submenu-is-open')) {
      const cooldownElapsed = now >= this.hideRevealCooldownUntil;
      const atBottom = scrollTop + window.innerHeight > document.body.clientHeight;
      const shouldHide = scrollDelta > 0 && scrollTop > this.scrollOffset + HIDE_OFFSET;
      const shouldReveal = scrollDelta < 0 && scrollTop > this.headerStart;

      if (cooldownElapsed && Math.abs(scrollDelta) > SCROLL_THRESHOLD) {
        if (atBottom || shouldHide) {
          this.hide();
        }
        else if (shouldReveal) {
          this.reveal();
        }
        this.hideRevealCooldownUntil = now + SCROLL_COOLDOWN_MS;
      }
    }

    this.lastScrollTop = scrollTop;
  }

  scrollAnimation() {
    const scrollTop = window.scrollY;
    const atBottom = scrollTop + window.innerHeight > document.body.clientHeight;

    if (!document.body.classList.contains('main-menu-is-open')) {
      if (atBottom || (scrollTop > this.lastScrollTop && scrollTop > this.scrollOffset)) {
        this.hide();
      }
      else if (scrollTop <= this.headerStart) {
        this.revealTop();
      }
      else if (scrollTop < this.lastScrollTop) {
        this.reveal();
      }
    }

    this.lastScrollTop = scrollTop;
    this.request = requestAnimationFrame(() => this.scrollAnimation());
  }

  initPopoverTriggers() {
    const useHover = !window.interestForElementSupported && document.documentElement.classList.contains('no-touch');

    this.popoverTriggers.forEach(trigger => {
      const popover = document.getElementById(trigger.getAttribute('popovertarget'));
      if (!popover) return;

      if (useHover) {
        let closeTimeout;

        const openPopover = () => {
          clearTimeout(closeTimeout);
          if (!popover.matches(':popover-open')) {
            popover.showPopover();
          }
        };

        const scheduleClose = () => {
          closeTimeout = setTimeout(() => {
            if (popover.matches(':popover-open')) {
              popover.hidePopover();
            }
          }, 100);
        };

        trigger.addEventListener('pointerenter', openPopover);
        trigger.addEventListener('pointerleave', scheduleClose);
        popover.addEventListener('pointerenter', () => clearTimeout(closeTimeout));
        popover.addEventListener('pointerleave', scheduleClose);
      }

      trigger.addEventListener('click', event => {
        if (popover.matches(':popover-open') && trigger.dataset?.url && trigger.dataset.url !== '#') {
          event.preventDefault();
          event.stopPropagation();
          window.location.href = trigger.dataset.url;
        }
      });
    });
  }

  hide() {
    document.body.classList.add('header-hidden', 'header-sticky');
    document.body.classList.remove('main-menu-is-open');
    this.closeSearch();
    this.closePopovers();
  }

  reveal() {
    document.body.classList.add('header-sticky');
    document.body.classList.remove('header-hidden');
  }

  revealTop() {
    document.body.classList.remove('header-hidden', 'header-sticky');
  }

  closeSearch() {
    if (this.search?.classList.contains('header__search--is-active')) this.searchToggle?.remove();
  }

  closePopovers() {
    this.popovers.forEach(popover => popover.hidePopover());
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}